// src/utils/slotTicket.js
//
// Slot ticket (docs/slot-story-spec.md Contract F): one slot as Markdown an
// engineer can read without opening the dashboard. Pure and offline - no fetch,
// no storage, no clipboard (the caller copies the text).
//
//   # <story.verdict>                       verbatim
//   - **Device/Platform/Unit/Sizes/Capture/Status**
//   > identifier warning                    only when a URL carries idfa/rdid/ppid/gpp
//   > request-only caveat                   only on a logcat slot
//   ## Steps    - **<Label>**: <state> + indented why[]
//   ## Requests one fenced URL per step that has a rowKey
//   Link: <origin>/?slot=<key>[&device=<id>][&har=<id>]
//   Link works while the session is open
//
// URLs are pasted verbatim on purpose (engineers reproduce with them); only
// backticks and pipes are percent-escaped so the fence and any table survive.

import { buildSlotStory } from './slotStory';
import { isLogcatLifecycle } from './slotHar';

export const IDENTIFIER_WARNING = 'Contains device identifiers; redact before sharing outside the team';
export const LINK_NOTE = 'Link works while the session is open';

// idfa= / rdid= / ppid= / gpp= as a query param or inside an (encoded) cust_params blob.
const IDENTIFIER_RE = /(?:[?&;,]|%26|%2C|%3B)(?:idfa|rdid|ppid|gpp)(?:=|%3D)/i;

/** Escape the two characters that break a Markdown fence or table cell. */
export function escapeUrlForMarkdown(url) {
  return String(url == null ? '' : url)
    .replace(/[\r\n]+/g, '')
    .replace(/`/g, '%60')
    .replace(/\|/g, '%7C');
}

export function urlHasIdentifiers(url) {
  return IDENTIFIER_RE.test(String(url || ''));
}

const lookupRow = (rowsByKey, k) => {
  if (k == null || !rowsByKey) return null;
  return (typeof rowsByKey.get === 'function' ? rowsByKey.get(k) : rowsByKey[k]) || null;
};

function truncationInfo(lc, gamRow) {
  const all = [
    ...(Array.isArray(lc && lc.issues) ? lc.issues : []),
    ...(gamRow && Array.isArray(gamRow.issues) ? gamRow.issues : []),
  ];
  const t = all.find((i) => i && i.code === 'ingest_url_truncated');
  if (!t && !(gamRow && gamRow.truncated)) return null;
  const m = t && /(\d+)\s*(?:chars|characters)/i.exec(t.msg || '');
  if (m) return { chars: m[1] };
  const len = gamRow && typeof gamRow.url === 'string' ? gamRow.url.length : 0;
  return { chars: len ? String(len) : null };
}

function hasRows(ph) {
  return !!(ph && (ph.fired || ph.rowKey != null || (Array.isArray(ph.rowKeys) && ph.rowKeys.length)));
}

/**
 * @param {object} story  buildSlotStory(lifecycle) (rebuilt from `lifecycle` when omitted)
 * @param {object} lifecycle  the slot lifecycle
 * @param {object} [opts]  { origin, deviceId, harId, device, platform, rowsByKey }
 * @returns {string} Markdown
 */
export function buildSlotTicket(story, lifecycle, opts = {}) {
  const lc = lifecycle || {};
  const st = story || buildSlotStory(lc);
  const o = opts || {};
  const phases = lc.phases || {};
  const gamRow = phases.gam && phases.gam.row;
  const usb = isLogcatLifecycle(lc);

  // Request URL per step that carries a rowKey.
  const requests = [];
  for (const s of st.steps || []) {
    if (s.rowKey == null) continue;
    const row = lookupRow(o.rowsByKey, s.rowKey);
    let url = row && row.url;
    if (!url && s.id === 'gam' && gamRow && gamRow.url) url = gamRow.url;
    requests.push({ label: s.label, rowKey: s.rowKey, url: url || null });
  }
  const identifiers = requests.some((r) => r.url && urlHasIdentifiers(r.url));

  const device = o.device || lc.device || (gamRow && (gamRow.deviceLabel || gamRow._deviceTab)) || 'unknown';
  const platform = o.platform || (gamRow && (gamRow.platformClass || gamRow.devicePlatform)) || 'unknown';
  const sizes = [].concat(st.sizes || []).filter(Boolean).join(', ') || 'unknown';

  const out = [];
  out.push(`# ${st.verdict}`);
  out.push('');
  out.push(`- **Device:** ${device}`);
  out.push(`- **Platform:** ${platform}`);
  out.push(`- **Unit:** ${st.adUnit || 'unknown'}`);
  out.push(`- **Sizes:** ${sizes}`);
  out.push(`- **Capture:** ${usb ? 'USB logcat' : 'MITM'}`);
  out.push(`- **Status:** ${st.status}`);
  out.push('');

  if (identifiers) {
    out.push(`> ${IDENTIFIER_WARNING}`);
    out.push('');
  }
  if (usb) {
    const trunc = truncationInfo(lc, gamRow);
    const noDemandRows = !hasRows(phases.prebid) && !hasRows(phases.amazon);
    let caveat = 'Request-only capture: GAM response is not visible';
    if (noDemandRows) caveat += '; Prebid and APS traffic are not visible';
    caveat += '.';
    if (trunc) caveat += ` URL cut${trunc.chars ? ` at ${trunc.chars} chars` : ''}; params after the cut are unknown.`;
    out.push(`> ${caveat}`);
    out.push('');
  }

  if (Array.isArray(st.slotWhy) && st.slotWhy.length) {
    out.push('## Slot');
    for (const w of st.slotWhy) out.push(`- ${w}`);
    out.push('');
  }

  out.push('## Steps');
  for (const s of st.steps || []) {
    out.push(`- **${s.label}**: ${s.state}`);
    for (const w of Array.isArray(s.why) ? s.why : []) out.push(`  - ${w}`);
  }
  out.push('');

  out.push('## Requests');
  if (!requests.length) {
    out.push('No request rows are linked to this slot.');
  } else {
    for (const r of requests) {
      out.push(`**${r.label}** (row ${r.rowKey})`);
      out.push('```');
      out.push(r.url ? escapeUrlForMarkdown(r.url) : '(request no longer in memory)');
      out.push('```');
    }
  }
  out.push('');

  const origin = String(o.origin || '').replace(/\/+$/, '');
  let link = `${origin}/?slot=${encodeURIComponent(st.key == null ? '' : st.key)}`;
  if (o.deviceId) link += `&device=${encodeURIComponent(o.deviceId)}`;
  if (o.harId) link += `&har=${encodeURIComponent(o.harId)}`;
  out.push(`Link: ${link}`);
  out.push(LINK_NOTE);
  out.push('');
  return out.join('\n');
}
